/**
 * Hapus umat and Hapus keluarga (release 31, owner 2026-11-02): a person or a family entered by
 * mistake ("data ganda", "salah ketik") is deleted, softly (`dihapus_pada`, with the reason in
 * `alasan_hapus`, schema 17), and leaves every list, count, report, map and search; its membership
 * rows (`anggota_keluarga`) go with it, and a person's sensitive fields (`umat_profil`). The app
 * offers no undo; the developer can revive a row through the op log.
 *
 * Who: an admin paroki for its own parish's people and families, the admin keuskupan as that
 * parish's admin paroki (plan 10); staff, ketua, uskup and dekan never. The push enforces it
 * (`periksaHapusData`, spec 9.5 step 4c): an op that deletes a live person, family or membership
 * from any other role is refused `role_cannot_write`, and a person's or a family's deletion
 * without its reason `invalid_fields`.
 *
 * Only for mistakes: a person can be deleted only while nothing official names them (no register
 * entry, no issued letter, no open mutasi) and while they head no family that still has other
 * members; a family only while it has no member left but the head deleted with it, no letter was
 * issued for it, and no mutasi is open on it. The device and the server each gather the facts
 * (apps/web/src/db/hapus.ts, apps/api/src/routes/sync/hapus.ts) and judge them here
 * (`halanganUmat`, `halanganKeluarga`), so the dialog lists what blocks and the push refuses the
 * same (`still_referenced`). A deleted family's parish number stays retired: it is never issued
 * again (release 14's rule, ./sync/ganda).
 */
import { type JenisSurat, JUDUL_BUKU, type TabelRegister } from './registers'
import type { Peran } from './schema/contract'
import { format, uiStrings } from './strings/index'
import { apply } from './sync/merge'
import { isDeleteOp, type LocalWrite, type Op, type Row } from './sync/ops'
import type { RejectReason } from './sync/reasons'
import type { Actor } from './sync/visibility'

const t = uiStrings.keluarga.hapus

/** The tables whose deletion is an admin's (`periksaHapusData`). */
export const TABEL_HAPUS_DATA: readonly string[] = ['umat', 'keluarga', 'anggota_keluarga']
/** The tables whose deletion carries a reason. */
export const TABEL_BERALASAN: readonly string[] = ['umat', 'keluarga']

/** The roles that delete a person or a family entered by mistake. */
export function bolehHapusData(peran: Peran | string | null | undefined): boolean {
  return peran === 'admin_paroki' || peran === 'admin_keuskupan'
}

const isi = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : null

/** Whether a stored row is deleted (`dihapus_pada` set). */
export function terhapus(row: Readonly<Record<string, unknown>> | null | undefined): boolean {
  return isi(row?.dihapus_pada) !== null
}

// ---------------------------------------------------------------------------
// What blocks a deletion
// ---------------------------------------------------------------------------

/** One reason a person or a family cannot be deleted. */
export type Halangan =
  /** A live register entry names the person. */
  | { jenis: 'buku'; tabel: TabelRegister; nomor: number | null }
  /** An issued letter names the person or the family. */
  | { jenis: 'surat'; surat: JenisSurat; nomor: string | null; keluarga: boolean }
  /** A mutasi on the person or the family is still open. */
  | { jenis: 'mutasi' }
  /** The person heads a family that has other live members. */
  | { jenis: 'kepala'; anggota: number }
  /** The family has live members besides the one deleted with it. */
  | { jenis: 'anggota'; anggota: number }

export interface EntriFakta {
  tabel: TabelRegister
  nomor: number | null
}

export interface SuratFakta {
  jenis: JenisSurat
  nomor: string | null
}

/** What the device or the server read of a person. */
export interface FaktaUmat {
  /** Live register entries naming the person (by `umat_id`, a groom or a bride, or the link). */
  entri: readonly EntriFakta[]
  /** Live letters naming the person. */
  surat: readonly SuratFakta[]
  /** An open mutasi (`diajukan`) on the person or the person's family. */
  mutasiTerbuka: boolean
  /** The other live members of a live family the person heads; 0 when they head none. */
  anggotaLainSebagaiKepala: number
}

/** What the device or the server read of a family. */
export interface FaktaKeluarga {
  /** Live letters issued for the family. */
  surat: readonly SuratFakta[]
  /** An open mutasi on the family. */
  mutasiTerbuka: boolean
  /** Live members besides the one deleted with it (its head, or its sole member). */
  anggotaLain: number
}

export function halanganUmat(f: FaktaUmat): Halangan[] {
  return [
    ...f.entri.map((e): Halangan => ({ jenis: 'buku', tabel: e.tabel, nomor: e.nomor })),
    ...f.surat.map(
      (s): Halangan => ({ jenis: 'surat', surat: s.jenis, nomor: s.nomor, keluarga: false }),
    ),
    ...(f.mutasiTerbuka ? [{ jenis: 'mutasi' } as const] : []),
    ...(f.anggotaLainSebagaiKepala > 0
      ? [{ jenis: 'kepala', anggota: f.anggotaLainSebagaiKepala } as const]
      : []),
  ]
}

export function halanganKeluarga(f: FaktaKeluarga): Halangan[] {
  return [
    ...(f.anggotaLain > 0 ? [{ jenis: 'anggota', anggota: f.anggotaLain } as const] : []),
    ...f.surat.map(
      (s): Halangan => ({ jenis: 'surat', surat: s.jenis, nomor: s.nomor, keluarga: true }),
    ),
    ...(f.mutasiTerbuka ? [{ jenis: 'mutasi' } as const] : []),
  ]
}

function labelSuratHapus(jenis: JenisSurat): string {
  return (uiStrings.enum.jenisSurat as Record<string, string>)[jenis] ?? jenis
}

/** The dialog's sentence for one reason: "Masih tercatat di Buku Baptis no. 12." */
export function kalimatHalangan(h: Halangan): string {
  switch (h.jenis) {
    case 'buku':
      return h.nomor === null
        ? format(t.halangan.bukuTanpaNomor, { buku: JUDUL_BUKU[h.tabel] })
        : format(t.halangan.buku, { buku: JUDUL_BUKU[h.tabel], nomor: h.nomor })
    case 'surat': {
      const surat = labelSuratHapus(h.surat)
      const nomor = h.nomor ?? '—'
      return h.keluarga
        ? format(t.halangan.suratKeluarga, { surat, nomor })
        : format(t.halangan.surat, { surat, nomor })
    }
    case 'mutasi':
      return t.halangan.mutasi
    case 'kepala':
      return format(t.halangan.kepala, { jumlah: h.anggota })
    case 'anggota':
      return format(t.halangan.anggota, { jumlah: h.anggota })
  }
}

/**
 * The push's detail for a refused deletion (`still_referenced`): fixed text by kind, never a number
 * or a name.
 */
export function detailHalangan(h: Halangan): string {
  switch (h.jenis) {
    case 'buku':
      return t.ditolak.buku
    case 'surat':
      return t.ditolak.surat
    case 'mutasi':
      return t.ditolak.mutasi
    case 'kepala':
      return t.ditolak.kepala
    case 'anggota':
      return t.ditolak.anggota
  }
}

// ---------------------------------------------------------------------------
// The writes
// ---------------------------------------------------------------------------

/** A person a deletion removes: the person, their membership rows, their profile row if any. */
export interface UmatDihapus {
  id: string
  anggotaKeluargaIds: readonly string[]
  /** A live `umat_profil` row exists (its id is the person's). */
  profil: boolean
}

/** What one Hapus removes: people first (a family's head), then the family. */
export interface RencanaHapus {
  umat: readonly UmatDihapus[]
  keluarga: { id: string; anggotaKeluargaIds: readonly string[] } | null
}

export type HasilAlasan = { ok: true; alasan: string } | { ok: false; galat: string }

/** The reason as typed: needed, trimmed. */
export function bacaAlasanHapus(teks: string): HasilAlasan {
  const alasan = isi(teks)
  return alasan === null ? { ok: false, galat: t.alasanWajib } : { ok: true, alasan }
}

/**
 * The writes of one Hapus, in the order the push judges them: each person (the stamp and the
 * reason), then their membership rows and profile; then the family, then its membership rows
 * not already gone. A membership row only after its person or its family, which the push requires.
 */
export function tulisanHapus(r: RencanaHapus, alasan: string, now: string): LocalWrite[] {
  const out: LocalWrite[] = []
  const sudah = new Set<string>()
  const hapusAnggota = (id: string) => {
    if (sudah.has(id)) return
    sudah.add(id)
    out.push({ table: 'anggota_keluarga', row_id: id, fields: { dihapus_pada: now } })
  }
  for (const u of r.umat) {
    out.push({ table: 'umat', row_id: u.id, fields: { dihapus_pada: now, alasan_hapus: alasan } })
    for (const a of u.anggotaKeluargaIds) hapusAnggota(a)
    if (u.profil) out.push({ table: 'umat_profil', row_id: u.id, fields: { dihapus_pada: now } })
  }
  if (r.keluarga !== null) {
    out.push({
      table: 'keluarga',
      row_id: r.keluarga.id,
      fields: { dihapus_pada: now, alasan_hapus: alasan },
    })
    for (const a of r.keluarga.anggotaKeluargaIds) hapusAnggota(a)
  }
  return out
}

// ---------------------------------------------------------------------------
// The push rule (spec 9.5 step 4c)
// ---------------------------------------------------------------------------

export type PeriksaHapus = { ok: true } | { ok: false; reason: RejectReason; detail: string }

/**
 * Whether the op deletes a stored row that is live. A row the server never stored (a creation it
 * refused as a duplicate, which the clerk deletes instead; final review C-1) is no deletion here.
 */
export function menghapusBaris(op: Op, current: Row | null): boolean {
  return isDeleteOp(op) && current !== null && !terhapus(current)
}

/**
 * An op that deletes a live person, family or membership is an admin paroki's (`actor` as
 * `pelakuPush` made it: the admin keuskupan writes a parish's records as its admin paroki); a
 * person's or a family's carries its reason. Every other op passes, so nothing an app before
 * release 31 sends is newly refused: it deletes none of these.
 */
export function periksaHapusData(actor: Actor, op: Op, current: Row | null): PeriksaHapus {
  if (!TABEL_HAPUS_DATA.includes(op.table) || !menghapusBaris(op, current)) return { ok: true }
  if (actor.peran !== 'admin_paroki') {
    return { ok: false, reason: 'role_cannot_write', detail: t.bukanAdmin }
  }
  if (TABEL_BERALASAN.includes(op.table)) {
    let row: Row
    try {
      row = apply(op, current).row
    } catch {
      return { ok: false, reason: 'invalid_fields', detail: 'stored row unreadable' }
    }
    if (isi(row.alasan_hapus) === null) {
      return { ok: false, reason: 'invalid_fields', detail: 'alasan_hapus required' }
    }
  }
  return { ok: true }
}
